import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { TOKENS } from "./styled";

export type StepState = "done" | "active" | "pending";

type Step = {
  index: number;
  label: string;
  sublabel?: string;
  state: StepState;
};

type Props = {
  steps: Step[];
  onStepClick?: (index: number) => void;
};

const StepperHeader: React.FC<Props> = ({ steps, onStepClick }) => {
  const total = steps.length;
  const doneCount = steps.filter((s) => s.state === "done").length;
  const progress =
    total === 0
      ? 0
      : ((doneCount + (steps.find((s) => s.state === "active") ? 0.5 : 0)) /
          total) *
        100;

  return (
    <Box
      component="nav"
      aria-label="Étapes de la réservation"
      sx={{
        position: "relative",
        bgcolor: TOKENS.WHITE,
        borderRadius: 3.5,
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        overflow: "hidden",
        mb: 3,
      }}
    >
      {/* progress bar */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          height: 3,
          width: `${progress}%`,
          background: TOKENS.GRADIENT_BLUE,
          transition: "width 0.4s ease",
        }}
      />
      <Box
        sx={{
          display: "grid",
          // On phones: collapse non-active steps to circle-only
          gridTemplateColumns: {
            xs: steps
              .map((s) => (s.state === "active" ? "1fr" : "auto"))
              .join(" "),
            sm: `repeat(${total}, 1fr)`,
          },
          alignItems: "stretch",
        }}
      >
        {steps.map((step) => {
          const active = step.state === "active";
          const done = step.state === "done";
          const clickable = done && !!onStepClick;
          return (
            <Box
              key={step.index}
              onClick={() => clickable && onStepClick?.(step.index)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: active ? 1 : 0, sm: 1.5 },
                px: { xs: active ? 1.5 : 1, sm: 2.5 },
                py: { xs: 1.25, sm: 2 },
                cursor: clickable ? "pointer" : "default",
                transition: "background 0.2s",
                "&:hover": clickable ? { bgcolor: TOKENS.GRAY_100 } : {},
                borderRight: `1px solid ${TOKENS.GRAY_200}`,
                "&:last-child": { borderRight: "none" },
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: "50%",
                  background: done
                    ? TOKENS.SUCCESS_GREEN
                    : active
                    ? TOKENS.GRADIENT_BLUE
                    : TOKENS.GRAY_200,
                  color:
                    done || active ? TOKENS.WHITE : TOKENS.GRAY_500,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 13,
                  fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: active
                    ? "0 4px 10px rgba(49,130,206,0.35)"
                    : "none",
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 16 }} /> : step.index}
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  display: { xs: active ? "block" : "none", sm: "block" },
                }}
              >
                <Typography
                  sx={{
                    color: active
                      ? TOKENS.ACCENT_BLUE
                      : done
                      ? TOKENS.GRAY_800
                      : TOKENS.GRAY_500,
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: 0.6,
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {step.label}
                </Typography>
                {step.sublabel && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: TOKENS.GRAY_600,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      fontSize: { xs: 12, sm: 13 },
                      display: { xs: "none", md: "block" },
                    }}
                  >
                    {step.sublabel}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StepperHeader;
